import { Component, Input, OnInit } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { NgForage } from 'ngforage';
import { Subject } from 'rxjs';
import AssetHelper from 'src/app/helpers/asset-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import Branch from 'src/app/model/branch/branch';
import {
  SubProject,
  SUB_PROJECT_TYPES,
} from 'src/app/model/project/sub-project';
import { User } from 'src/app/model/user';
import { OfflineBackedBranchChoicesService } from 'src/app/services/branch/offline-backed-branch-choices.service';
import { ImmoIPNotExecutedHistoryItemObject } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-not-executed-history-item-object';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { OfflineStorageTourHelper } from '../../tour-view/offline-storage/helper/offline-storage-tour-helper';
import { ImmoIPPlacerTourItemDetailChangeStorageHandler } from '../helper/immo-ip-placer-tour-item-detail-change-storage-handler';
import { ImmoIPPlacerImmoTaskChangesFunctions } from '../immo-task/helper/change-storage-handler/immo-ip-placer-immo-task-change-functions';
import { ImmoIPPlacerTourItemDetailNotExecutedModalHelper } from './immo-ip-placer-tour-item-detail-not-executed-modal-helper';

@Component({
  selector: 'immo-ip-placer-tour-item-detail-not-executed-modal',
  templateUrl:
    './immo-ip-placer-tour-item-detail-not-executed-modal.component.html',
  styleUrls: [
    './immo-ip-placer-tour-item-detail-not-executed-modal.component.scss',
  ],
})
export class ImmoIPPlacerTourItemDetailNotExecutedModalComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler;

  @Input()
  subProject: SubProject;

  @Input()
  disabled: boolean = false;

  @Input()
  shouldReloadNotExecutedHistoryItemAssets: Subject<void>;

  @Input()
  user: User;

  notExecutedHistoryItemObject: ImmoIPNotExecutedHistoryItemObject;

  branchChoices: Branch[] = [];

  stockedAtBranch: boolean | null = null;
  stockedAtBranchSubtitle: string;

  private addedAssetUuids: string[];

  SUB_PROJECT_TYPES = SUB_PROJECT_TYPES;

  constructor(
    public dialogRef: MatDialogRef<
      ImmoIPPlacerTourItemDetailNotExecutedModalComponent,
      ImmoIPNotExecutedHistoryItemObject
    >,
    private branchChoicesService: OfflineBackedBranchChoicesService,
    private ngForage: NgForage,
    private snackService: SnackService
  ) {
    super();
  }

  ngOnInit(): void {
    this.notExecutedHistoryItemObject =
      this.immoIPPlacerTourItemDetailChangeStorageHandler.getNotExecutedHistoryItemObject();
    this.stockedAtBranchSubtitle =
      ImmoIPPlacerTourItemDetailNotExecutedModalHelper.getStockedAtBranchSubtitle(
        this.subProject.type
      );

    if (
      this.notExecutedHistoryItemObject != null &&
      this.notExecutedHistoryItemObject.id != null
    ) {
      this.stockedAtBranch =
        this.notExecutedHistoryItemObject.stockAtBranchId != null;
    }

    this.addSubscription(
      this.branchChoicesService.getBranchChoicesSubscribable(),
      (branches: Branch[]) => (this.branchChoices = branches)
    );
    this.addSubscription(
      this.dialogRef.beforeClosed(),
      (output: ImmoIPNotExecutedHistoryItemObject) => {
        if (output == null) {
          // the HistoryItem created in the modal will not be submitted,
          // therefore we should clean up any assets that may have been stored locally
          this.cleanupAssets();
        }
      }
    );
  }

  contentChanged(content: string): void {
    this.notExecutedHistoryItemObject.content = content;
  }

  clientCommunicationSummaryChanged(clientCommunicationSummary: string): void {
    this.notExecutedHistoryItemObject.clientCommunicationSummary =
      clientCommunicationSummary;
  }

  inpressCommunicationSummaryChanged(
    inpressCommunicationSummary: string
  ): void {
    this.notExecutedHistoryItemObject.inpressCommunicationSummary =
      inpressCommunicationSummary;
  }

  suggestedFollowUpChanged(suggestedFollowUp: string): void {
    this.notExecutedHistoryItemObject.suggestedFollowUp = suggestedFollowUp;
  }

  setStockedAtBranch(stockedAtBranch: boolean): void {
    this.stockedAtBranch = stockedAtBranch;
    if (!stockedAtBranch) {
      this.notExecutedHistoryItemObject.stockAtBranchId = null;
    }
  }

  setCallInpress(callInpress: boolean): void {
    this.notExecutedHistoryItemObject.callInpress = callInpress;
    this.notExecutedHistoryItemObject.inpressCommunicationRegistration =
      ImmoIPPlacerImmoTaskChangesFunctions.initializeExecutedRegistration(
        new Date(),
        this.user
      );
    if (!callInpress) {
      this.notExecutedHistoryItemObject.inpressCommunicationSummary = null;
    }
  }

  setCallClient(callClient: boolean): void {
    this.notExecutedHistoryItemObject.callClient = callClient;
    this.notExecutedHistoryItemObject.clientCommunicationRegistration =
      ImmoIPPlacerImmoTaskChangesFunctions.initializeExecutedRegistration(
        new Date(),
        this.user
      );
    if (!callClient) {
      this.notExecutedHistoryItemObject.clientCommunicationSummary = null;
    }
  }

  addedAssetUuidsChanged(assetUuids: string[]): void {
    this.addedAssetUuids = assetUuids;
    if (this.shouldReloadNotExecutedHistoryItemAssets != null) {
      this.shouldReloadNotExecutedHistoryItemAssets.next();
    }
  }

  submitNotExecuted(): void {
    if (
      this.stockedAtBranch &&
      this.notExecutedHistoryItemObject.stockAtBranchId == null &&
      this.subProject.type !== 'Repo'
    ) {
      this.snackService.showWarning('Gelieve een filiaal te selecteren');
      return;
    } else if (this.notExecutedHistoryItemObject.callInpress === null) {
      this.snackService.showWarning(
        'Gelieve een optie aan te duiden onder "Bellen met Inpress"'
      );
      return;
    } else if (this.notExecutedHistoryItemObject.callClient === null) {
      this.snackService.showWarning(
        'Gelieve een optie aan te duiden onder "Bellen met makelaar"'
      );
      return;
    }
    this.dialogRef.close(this.notExecutedHistoryItemObject);
  }

  private cleanupAssets(): void {
    const executed = OfflineStorageTourHelper.getSubProjectExecutionState(
      this.subProject
    );
    if (
      executed == null &&
      CollectionHelper.isPopulated(this.addedAssetUuids)
    ) {
      this.addedAssetUuids.forEach((uuid) =>
        this.ngForage.removeItem(AssetHelper.getStorageKeyFor(uuid))
      );
    }
  }
}
